/**
 * Avatar — pixel-faithful port of `design/Avatar.dc.html` (DC-RUNTIME §6.1).
 *
 * Contract notes for the screen lanes:
 *  - Every numeric prop accepts `number | string`; the DC runtime passed
 *    unbraced attributes through as strings (`size="44"`) and the component
 *    relied on `"44" + 'px'`. Both spellings render identically here.
 *  - Defaults use `??`, never `||`, so `0` / `''` / `false` survive.
 *  - `style` is the **host wrapper** style: `<dc-import style="…">` never
 *    reached the component, it was filtered to positional properties and put on
 *    a generated wrapper div (DC-RUNTIME §4). Pass positional CSS only
 *    (`position/inset/left/right/top/bottom/width/height/z-index/transform`).
 *  - The initials are ALWAYS rendered; `hasAv` paints the avatar layer over
 *    them. Feed `ini` / `bg` / `hasAv` / `avBg` straight from `chipOf(u)`
 *    (`lib/format.ts`) or any selector field spelled `*Ini` / `*HueBg` /
 *    `*HasAv` / `*AvBg`.
 *
 * Call-site sizes in the prototype: 18, 20, 22, 26, 30, 36, 44, 48
 * (INVENTORY §11).
 */

import type { CSSProperties } from 'react';

import { inkOn } from '../lib/color.ts';

export interface AvatarProps {
  /** box px — DC default 26 */
  size?: number | string;
  /** pixel-font px — DC default 8 */
  fs?: number | string;
  /** initial letter(s) */
  ini?: string;
  /** `oklch(0.62 0.13 <hue>)` from `hueBg()` */
  bg?: string;
  hasAv?: boolean;
  /** CSS background shorthand, e.g. `url("data:…") center/cover no-repeat` */
  avBg?: string;
  /** native tooltip; DC default `''` */
  title?: string;
  /** DC default `1px solid var(--gt-ink)` */
  bd?: string;
  /** host-wrapper style — positional properties only */
  style?: CSSProperties;
}

export function Avatar({ size, fs, ini, bg, hasAv, avBg, title, bd, style }: AvatarProps) {
  return (
    <div className="sc-host" data-sc-name="Avatar" style={style}>
      <div
        title={title ?? ''}
        style={{
          position: 'relative',
          width: (size ?? 26) + 'px',
          height: (size ?? 26) + 'px',
          background: bg,
          border: bd ?? '1px solid var(--gt-ink)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--gt-font-pixel)',
          fontSize: (fs ?? 8) + 'px',
          /* Adaptive since 3012: player colors are no longer one lightness —
             the custom field reaches sRGB's dark edge — so a hardcoded
             `--gt-ink` initial measured 1.7:1 on a deep pick; `inkOn` returns
             the token that actually reads (10.4:1 on the same pick). Derived
             from the bg HERE so every call site is covered without an API
             change; a non-color bg (the FORMER FRIEND border token) falls
             back to dark ink, which is the pre-3012 behaviour.
             `design/gt-logic.js#inkOn`, DESIGN-CHANGELOG 2026-08-18. */
          color: inkOn(bg ?? ''),
          overflow: 'hidden',
          flex: 'none',
        }}
      >
        <span>{ini ?? ''}</span>
        {hasAv ? <div style={{ position: 'absolute', inset: 0, background: avBg }} /> : null}
      </div>
    </div>
  );
}
